'use client'

const BINS = [
  { label: '0 – 5 ans', pct: 0.3, count: 4, color: 'teal' },
  { label: '6 – 10 ans', pct: 3.4, count: 42, color: 'olive' },
  { label: '11 – 15 ans', pct: 25.4, count: 317, color: 'amber' },
  { label: '21+ ans', pct: 70.9, count: 887, color: 'red' },
]

function binFillClass(color: string) {
  switch (color) {
    case 'teal':
      return 'bg-gradient-to-b from-[#AEEBE3] via-[#7DDBD0] to-[#4DBFB2]'
    case 'olive':
      return 'bg-gradient-to-b from-[#DCE8C8] via-[#BFD49B] to-[#93B56A]'
    case 'amber':
      return 'bg-gradient-to-b from-[#FFE7B3] via-[#FFD07A] to-[#F2B35D]'
    case 'orange':
      return 'bg-gradient-to-b from-[#FFD4C2] via-[#F5A77E] to-[#E37B3B]'
    case 'red':
      return 'bg-gradient-to-b from-[#F7B0B0] via-[#F07A7A] to-[#DE4B4B]'
    case 'gray':
    default:
      return 'bg-gradient-to-b from-[#E9EDF3] via-[#D6DDE8] to-[#C3CBD9]'
  }
}

function binSolidColor(color: string) {
  switch (color) {
    case 'teal':
      return '#4DBFB2'
    case 'olive':
      return '#93B56A'
    case 'amber':
      return '#F2B35D'
    case 'orange':
      return '#E37B3B'
    case 'red':
      return '#DE4B4B'
    default:
      return '#D6DDE8'
  }
}

function parseRange(label: string) {
  const cleaned = label.replace('ans', '').trim()
  const plus = cleaned.match(/^(\d+)\s*\+$/)
  if (plus) return { lo: Number(plus[1]), hi: null as number | null }
  const range = cleaned.match(/^(\d+)\s*[–-]\s*(\d+)$/)
  if (range) return { lo: Number(range[1]), hi: Number(range[2]) }
  return { lo: null as number | null, hi: null as number | null }
}

function formatPct(n: number) {
  return `${Math.round(n * 10) / 10}%`
}

function formatAvgAge(n: number) {
  const oneDec = Math.round(n * 10) / 10
  return String(oneDec).replace('.', ',')
}

export default function VehicleAgeSection() {
  const maxPct = Math.max(...BINS.map((b) => b.pct))
  const sumPct = BINS.reduce((s, b) => s + b.pct, 0)

  const pctOver15 =
    Math.round(
      BINS.reduce((s, b) => {
        const { lo, hi } = parseRange(b.label)
        const isOver15 =
          (lo !== null && lo >= 16) || (hi === null && lo !== null && lo >= 16) || b.label.includes('+')
        return s + (isOver15 ? b.pct : 0)
      }, 0) * 10
    ) / 10

  const avgAge =
    BINS.reduce((s, b) => {
      const { lo, hi } = parseRange(b.label)
      let rep = 0
      if (lo !== null && hi !== null) rep = (lo + hi) / 2
      else if (lo !== null && hi === null) rep = lo
      return s + rep * b.pct
    }, 0) / (sumPct || 1)

  const Y_TOP = 1250
  const Y_MID = Math.round(Y_TOP / 2)

  const segments = BINS.map((b) => ({
    key: b.label,
    width: `${b.pct}%`,
    bg: binSolidColor(b.color),
  }))

  const LABEL_STRIP_H = 34
  const CHART_H = 185
  const PLOT_H = CHART_H - LABEL_STRIP_H

  const BAR_W = 110
  const PCT_CAP_H = 40
  const MIN_FILL_BELOW_CAP = 6
  const MIN_BAR_H = PCT_CAP_H + MIN_FILL_BELOW_CAP // ✅ minimum just enough to show the cap + tiny fill
  const MAX_BAR_H = PLOT_H

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-5">
      <div className="flex items-start justify-between gap-6">
        <h3 className="text-[26px] font-extrabold text-slate-900">Âge du parc automobile</h3>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left: chart */}
        <div className="lg:col-span-2">
          <div className="relative rounded-2xl bg-white">
            <div className="relative h-[185px]">
              {/* Y axis labels */}
              <div className="absolute left-0 top-0 bottom-0 w-[54px] flex flex-col justify-between text-slate-400 font-semibold">
                <div className="text-[14px] leading-none">{Y_TOP}</div>
                <div className="text-[14px] leading-none">{Y_MID}</div>
                <div className="text-[14px] leading-none">0</div>
              </div>

              {/* Plot area */}
              <div className="absolute left-[54px] right-0 top-0 bottom-0">
                {/* grid lines */}
                <div className="absolute left-0 right-0 top-0" style={{ bottom: LABEL_STRIP_H }}>
                  <div className="h-full flex flex-col justify-between">
                    <div className="h-px bg-slate-100" />
                    <div className="h-px bg-slate-100" />
                    <div className="h-px bg-slate-100" />
                  </div>

                  {/* x-axis */}
                  <div className="absolute left-0 right-0 bottom-0 h-px bg-slate-200" />
                </div>

                {/* Bars */}
                <div className="absolute left-0 right-0 flex items-end justify-between gap-6 px-4" style={{ bottom: LABEL_STRIP_H }}>
                  {BINS.map((b) => {
                    // ✅ now small bins will differ, because min isn't forcing them all to 52
                    const barHRaw = Math.round((b.pct / maxPct) * MAX_BAR_H)
                    const barH = Math.min(MAX_BAR_H, Math.max(MIN_BAR_H, barHRaw))
                    const fillBelow = Math.max(MIN_FILL_BELOW_CAP, barH - PCT_CAP_H)

                    return (
                      <div key={b.label} className="flex-1 flex flex-col items-center">
                        <div
                          className={`rounded-[10px] border border-slate-200 overflow-hidden shadow-[0_10px_18px_rgba(15,23,42,0.06)] ${binFillClass(
                            b.color
                          )}`}
                          style={{ width: BAR_W, height: barH }}
                        >
                          <div className="h-[40px] bg-white/55 backdrop-blur-[2px] flex flex-col items-center justify-center">
                            <span className="text-[16px] font-extrabold text-slate-900 leading-none">{formatPct(b.pct)}</span>
                            <span className="mt-[2px] text-[11px] font-semibold text-slate-700 leading-none">
                              {b.count} taxis
                            </span>
                          </div>

                          <div style={{ height: fillBelow }} />
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Labels under axis */}
                <div className="absolute left-0 right-0 bottom-0 h-[34px] flex items-end justify-between gap-6 px-4">
                  {BINS.map((b) => (
                    <div key={`lbl-${b.label}`} className="flex-1 flex justify-center">
                      <div className="text-[13px] font-semibold text-slate-500 text-center whitespace-nowrap">
                        {b.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: KPIs + segmented bar */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex">
              <div className="p-5 flex-[1.25]">
                <p className="text-[13px] font-semibold text-slate-500">Âge moyen</p>
                <p className="mt-3 text-[34px] font-extrabold text-slate-900 leading-none">
                  {formatAvgAge(avgAge)} <span className="text-[16px] font-bold text-slate-700">ans</span>
                </p>
              </div>

              <div className="p-5 border-l border-slate-200 flex-1">
                <p className="text-[13px] font-semibold text-slate-500">% &gt; 15 ans</p>
                <p className="mt-3 text-[34px] font-extrabold text-slate-900 leading-none">
                  {formatPct(pctOver15).replace('.0%', '%')}
                </p>
              </div>
            </div>

            <div className="px-5 pb-5 pt-3">
              <div className="h-10 rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                <div className="h-full flex">
                  {segments.map((s) => (
                    <div key={s.key} className="h-full" style={{ width: s.width, backgroundColor: s.bg }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* end right */}
      </div>
    </section>
  )
}
